'use strict';

import User from '../models/User';
import authModalLoader from '../../auth/authModalLoader';
import tracking from 'dibs-tracking';
import Favorite from 'bunsen/models/identity/Favorite';
import calloutTracking from '../../components/Callout/calloutTracking';
import chatUtils from '../utils/social/chat';
import FavoritesStore from '../stores/FavoritesStore';

const favoritesActions = {
    _logInUser(actionContext, payload, done) {
        const {authModalOptions} = payload;
        // Default options
        let modalOptions = {
            action: authModalLoader.constants.REGISTER,
            flow: authModalLoader.constants.FOLLOW_DEALER_FLOW,
            ga: {
                label: ''
            }
        };
        let promise;

        modalOptions = Object.assign({}, modalOptions, authModalOptions);

        if (calloutTracking.isActive()) {
            const label = modalOptions.ga.label + ' - callout';
            modalOptions = Object.assign({}, modalOptions, {
                ga: {label}
            });
        }

        if (User.loggedIn()) {
            promise = User.load();
        } else {
            promise = authModalLoader.show(modalOptions);
        }

        promise
            .then(() => done())
            .catch(err => done(err));
    },

    /**
     * Fetching saved collection
     * @param {Fluxible context} actionContext
     * @param {Object} payload
     * @param {String} payload.uri Url of the page which we want to follow
     */
    fetchFavorites(actionContext, payload, done) {
        const {type = '', emailToken, pageSize} = payload;

        /**
         * If user is logged in - we fetch favorites collection, to get state of the CTA
         */
        if (User.loggedIn()) {
            const store = actionContext.getStore(FavoritesStore);
            const collection = store.getFavoritesCollection();

            collection.setParam('userIds', User.getUserId());
            collection.setParam('types', type);
            collection.setParam('pageSize', pageSize);

            if (emailToken) {
                collection.setParam('emailToken', emailToken);
            }

            actionContext.dispatch('favorites:fetch:start');

            collection
                .fetchWithParameters()
                .always(() => {
                    actionContext.dispatch('favorites:fetch:end');
                    done(null, collection.toJSON());
                });
        } else {
            done();
        }
    },

    /**
     * Save favorite search
     * @param {Fluxible context} actionContext
     * @param {Object} payload
     * @param {String} payload.tracking         GA tracking object
     * @param {String} payload.query            search query
     * @param {String} payload.page             page url
     * @param {String} payload.type             favorite type: search/dealer
     * @returns {Promise} resolves with saved model
     */
    saveToFavorites(actionContext, payload, done) {
        const {type, solrQuery, page} = payload;

        actionContext
            .executeAction(favoritesActions._logInUser, payload, err => {
                if (err) {
                    chatUtils.track.abandonFavorite(type);
                    actionContext.dispatch('favorite:canceled');
                    done();
                } else {
                    let promise = Promise.resolve(payload);

                    if (type === Favorite.TYPE_SEARCH && (!solrQuery || !page)) {
                        promise = actionContext.executeAction(favoritesActions.fetchDataForFavoriting, payload);
                    }

                    promise.then((response) => {
                        const savePayload = Object.assign({}, payload, response);
                        actionContext.executeAction(favoritesActions._saveFavorite, savePayload, done);
                    });
                }
            });
    },

    _saveFavorite(actionContext, payload, done) {
        const {solrQuery, page, type, name, primaryKey} = payload;
        const Model = actionContext.getStore(FavoritesStore).getFavoriteModelInstance();

        const model = new Model({
            user: {
                id: User.getUserId()
            },
            type
        });

        switch (type) {
            case Favorite.TYPE_SEARCH:
                model.set({
                    query: solrQuery,
                    page
                });
                break;

            case Favorite.TYPE_DESIGNER:
                model.set({page, name});
            case Favorite.TYPE_DEALER:
                model.set({
                    anchor: {
                        sourceId: primaryKey,
                        sourceType: type
                    }
                });
                break;
        }

        model
            .save()
            .then(() => {
                const trackingPayload = payload.tracking;
                if (trackingPayload) {
                    let {label} = trackingPayload;

                    if (calloutTracking.isActive()) {
                        label += ' - callout';
                    }

                    tracking.trackEvent(Object.assign({}, trackingPayload, {label}));
                }
                actionContext.dispatch('favorites:favorite:add', model);
                done(null, model);
            })
            .fail(error => {
                actionContext.dispatch('favorites:error', error);
                done(error);
            });
    },

    /**
     * Remove saved favorite search
     * @param {Fluxible context} actionContext
     * @param {Object} payload
     * @param {Number} payload.id           Favorite model id
     * @param {String} payload.tracking     GA tracking object
     * @returns {Promise} resolves with removed model
     */
    removeFromFavorites(actionContext, payload, done) {
        const {id} = payload;

        actionContext
            .executeAction(favoritesActions._logInUser, payload, err => {
                // User failed to login
                if (err) {
                    done(err);
                } else {
                    const model = actionContext.getStore(FavoritesStore).getFavoriteModel(id);

                    if (model) {
                        model.destroy()
                            .then(() => {
                                if (payload.tracking) {
                                    tracking.trackEvent(payload.tracking);
                                }

                                actionContext.dispatch('favorites:favorite:remove', model);
                                done(null, model);
                            })
                            .fail(error => {
                                actionContext.dispatch('favorites:error', error);
                                done(error);
                            });
                    } else {
                        done(`Favorite with id "${id}" was not found.`);
                    }
                }
            });
    },

    /**
     * Fetch data for favoriting/saving
     *
     * @param {Object} actionContext
     * @param {Object} payload
     * @param {function} done
     * @returns {Promise}
     */
    fetchDataForFavoriting(actionContext, payload, done) {
        const model = actionContext.getStore(FavoritesStore).getSaveSearchModel();
        const {type} = payload;

        model.getSaveSearchData(payload).then(response => {
            const {sq: solrQuery, uri: page} = response;
            done(null, {solrQuery, page, type});
        });
    }


};

module.exports = favoritesActions;



// WEBPACK FOOTER //
// ./assets/js/global/actions/favoritesActions.es.js